import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import { openCrispChat } from '../CrispChat';
import { localizedPath, normalizeLang } from '../../lib/contentLocale';
import '../LandingPage.css';
import './SiteLayout.css';

function LearnNav({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = normalizeLang(i18n.language);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const items = [
    { to: localizedPath('/foods', lang), label: t('header.nav.foods') },
    { to: localizedPath('/recipes', lang), label: t('header.nav.recipes') },
    { to: localizedPath('/guides', lang), label: t('header.nav.guides') },
  ];
  const isActive = items.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className={`nav-learn ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`nav-link nav-learn-toggle ${isActive ? 'active' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        {t('header.nav.learn')}
      </button>
      <div className="nav-learn-menu" role="menu">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            role="menuitem"
            className={`nav-learn-item${
              location.pathname === item.to || location.pathname.startsWith(`${item.to}/`) ? ' active' : ''
            }`}
            onClick={() => {
              setOpen(false);
              onNavigate();
            }}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function SiteLayout({ children, homeHeader = false, onHomeNav }) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  const handleHomeNav = (id) => {
    closeMenu();
    if (onHomeNav) onHomeNav(id);
  };

  return (
    <div className="site-shell">
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu} />}
      <header className="landing-header">
        <div className="header-container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src="/icon.png" alt="Tiny Spoon" className="logo-icon" />
            <h1>{t('header.logo')}</h1>
          </Link>
          <button
            className={`burger-menu ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
          <nav className={`main-nav ${isMenuOpen ? 'active' : ''}`}>
            {homeHeader ? (
              <button onClick={() => handleHomeNav('features')} className="nav-link">
                {t('header.nav.features')}
              </button>
            ) : null}
            <LearnNav onNavigate={closeMenu} />
            {homeHeader ? (
              <button type="button" className="nav-link" onClick={() => handleHomeNav('download')}>
                {t('header.nav.download')}
              </button>
            ) : (
              <a href="#download-app" className="nav-link" onClick={closeMenu}>
                {t('header.nav.download')}
              </a>
            )}
            <a
              href={`mailto:${t('footer.email')}`}
              className="nav-link"
              onClick={closeMenu}
            >
              {t('header.nav.contact')}
            </a>
            <LanguageSwitcher />
          </nav>
        </div>
      </header>
      {children}
      <SiteFooter />
    </div>
  );
}

export function SiteFooter({ lang: langProp }) {
  const { t, i18n } = useTranslation();
  const lang = langProp || normalizeLang(i18n.language);
  const link = (path) => localizedPath(path, lang);

  return (
    <footer className="landing-footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{t('footer.learn')}</h3>
            <Link to={link('/foods')} className="footer-link">{t('header.nav.foods')}</Link>
            <Link to={link('/guides')} className="footer-link">{t('header.nav.guides')}</Link>
            <Link to={link('/meal-plans')} className="footer-link">{t('header.nav.mealPlans')}</Link>
            <Link to={link('/recipes')} className="footer-link">{t('header.nav.recipes')}</Link>
          </div>
          <div className="footer-section">
            <h3>{t('footer.download')}</h3>
            <a href={t('footer.appStoreUrl')} target="_blank" rel="noopener noreferrer" className="footer-link">
              App Store
            </a>
            <a href={t('footer.playStoreUrl')} target="_blank" rel="noopener noreferrer" className="footer-link">
              Google Play
            </a>
          </div>
          <div className="footer-section">
            <h3>{t('footer.legal')}</h3>
            <Link to="/privacy" className="footer-link">{t('footer.privacy')}</Link>
            <Link to="/terms" className="footer-link">{t('footer.terms')}</Link>
            <Link to="/disclaimer" className="footer-link">{t('footer.disclaimer')}</Link>
          </div>
          <div className="footer-section">
            <h3>{t('footer.contact')}</h3>
            <button type="button" onClick={openCrispChat} className="footer-link">
              {t('footer.liveChat')}
            </button>
            <a href={`mailto:${t('footer.email')}`} className="footer-link">
              {t('footer.email')}
            </a>
            <a href="https://t.me/tinyspoonbot" target="_blank" rel="noopener noreferrer" className="footer-link">
              Telegram @tinyspoonbot
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
          <p className="footer-company">{t('footer.company')}</p>
        </div>
      </div>
    </footer>
  );
}

export default SiteLayout;
